import { useEffect, useRef, useState } from 'react'
import {
  ApiError, deviceMe, deviceSignout, listLiceCounts, withdrawLiceCount,
  type DeviceMe, type LiceCount, type LiceTreatment,
} from '../api'
import { Telling, datoLang } from '../components/Telling'
import { ErrorBox, Rows } from '../components/bits'
import { LangSwitch, useT } from '../lang'
import { navigate } from '../router'
import { NameForm } from './Koble'

/* The screen that is ABOUT being a device.
 *
 * It used to be all a device ever saw. Now an admitted, named device carries on
 * into the customer portal (see Gate in app.tsx, and the receiver's
 * device-aware reads), and this is where a device lands only while it has
 * nothing else to show: waiting for a manager to approve it, or not yet named.
 * It stays reachable at /enhet for a working tablet, because it holds the only
 * way to rename a device and the only way to disconnect one.
 */

export function Enhet() {
  const t = useT()
  const [me, setMe] = useState<DeviceMe | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [siteId, setSiteId] = useState<string | null>(null)
  // Whether this screen was showing because the device was held -- waiting or
  // unnamed. Gate reads /v1/me once, so when the hold lifts nothing else would
  // notice and the tablet would sit here until somebody reloaded it. A device
  // that opened /enhet while already in is never held, and is not moved.
  const held = useRef(false)

  const load = async () => {
    try {
      const d = await deviceMe()
      if ((d.waiting && !d.approved) || d.needs_name) held.current = true
      else if (held.current && d.approved) {
        window.location.assign('/')
        return
      }
      setMe(d)
      // One farm is the overwhelmingly common case, and a chooser with one
      // option in it is a question nobody should be asked.
      setSiteId((prev) => prev ?? (d.sites.length === 1 ? d.sites[0]!.site_id : null))
    } catch (err) {
      if (err instanceof ApiError) setError(err)
    }
  }
  useEffect(() => { void load() }, [])

  /* Poll only while waiting. A device that is in has nothing to watch for, and
     a device that is out is the one somebody is standing over. */
  useEffect(() => {
    if (!me || me.approved || !me.waiting) return
    const timer = window.setInterval(() => { void load() }, 5000)
    return () => window.clearInterval(timer)
  }, [me?.approved, me?.waiting])

  if (error) return <Wrap><ErrorBox error={error} /></Wrap>
  if (!me) return <Wrap><Rows n={3} /></Wrap>

  // Asked here as well as after enrolling, because a browser reloaded mid
  // answer, or a QR scanned in a hurry, otherwise never gets asked again.
  if (me.needs_name) {
    return (
      <Wrap>
        <NameForm onNamed={async () => { await load() }} />
      </Wrap>
    )
  }

  /* WAITING, checked before "no farms".
   *
   * Both states have an empty `sites`, and they are the same fact seen from
   * two ends: a device waiting for approval has no membership, which is
   * exactly why it can see nothing. The order matters because the sentences
   * are opposite advice. "Ask for a new code" is right for a device whose
   * grant has been taken away and catastrophically wrong for one that is two
   * minutes into a perfectly normal wait -- it sends somebody back to the
   * connect screen to type the code they just typed. */
  if (me.waiting && !me.approved) {
    return (
      <Wrap>
        <div className="card stack" style={{ gap: 14 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>{t('Waiting to be let in')}</h2>
          <p className="muted" style={{ margin: 0, fontSize: 14 }}>
            {t('Your manager has been asked to approve this device for')}{' '}
            <strong>{me.waiting_for ?? me.org_name}</strong>.
          </p>
          <div className="notice">
            {t('You can leave this screen open. It will start on its own.')}
          </div>
          <button className="btn" onClick={() => void load()}>
            {t('Check again')}
          </button>
        </div>
        <Foot />
      </Wrap>
    )
  }

  if (me.sites.length === 0) {
    return (
      <Wrap>
        <Head me={me} onReload={load} />
        <div className="notice bad">
          {t('This device is not connected to a farm. Ask for a new code.')}
        </div>
        <Foot />
      </Wrap>
    )
  }

  if (!siteId) {
    return (
      <Wrap>
        <Head me={me} onReload={load} />
        <div className="card stack" style={{ gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>{t('Which farm?')}</h2>
          {me.sites.map((s) => (
            <button key={s.site_id} className="btn"
              style={{ justifyContent: 'flex-start', fontSize: 17, padding: '14px 16px' }}
              onClick={() => setSiteId(s.site_id)}>
              {s.name}
            </button>
          ))}
        </div>
        <Foot />
      </Wrap>
    )
  }

  const site = me.sites.find((s) => s.site_id === siteId)
  return (
    <Wrap>
      <Head me={me} onReload={load} />
      <Counting
        siteId={siteId}
        siteName={site?.name ?? siteId}
        canCount={me.can.lice_count}
        onChangeSite={me.sites.length > 1 ? () => setSiteId(null) : null}
      />
      <Foot />
    </Wrap>
  )
}

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <div className="device-wrap">
      <div className="device">{children}</div>
    </div>
  )
}

function Head({ me, onReload }: { me: DeviceMe; onReload: () => Promise<void> }) {
  const t = useT()
  const [renaming, setRenaming] = useState(false)
  return (
    <div className="device-head">
      <div className="wordmark">
        <span className="dot" aria-hidden="true" />
        Phoniq
      </div>
      {renaming ? (
        <NameForm
          lead={t('Everything you enter is recorded under this name.')}
          submitLabel={t('Save')}
          onNamed={async () => { await onReload(); setRenaming(false) }}
        />
      ) : (
        <button className="btn ghost sm" onClick={() => setRenaming(true)}
          title={t('Change the name on this device')}>
          {me.display_name || me.code_label || t('Name this device')}
        </button>
      )}
    </div>
  )
}

/* --------------------------------------------------------------- counting */

function Counting({
  siteId, siteName, canCount, onChangeSite,
}: {
  siteId: string
  siteName: string
  canCount: boolean
  onChangeSite: (() => void) | null
}) {
  const t = useT()
  const [counts, setCounts] = useState<LiceCount[] | null>(null)
  const [treatments, setTreatments] = useState<LiceTreatment[]>([])
  const [error, setError] = useState<ApiError | null>(null)
  const [adding, setAdding] = useState<string | null>(null)

  const load = async () => {
    try {
      const r = await listLiceCounts(siteId)
      setCounts(r.items)
      setTreatments(r.treatments ?? [])
    } catch (err) {
      if (err instanceof ApiError) setError(err)
    }
  }
  useEffect(() => { setCounts(null); void load() }, [siteId])

  const today = new Date().toISOString().slice(0, 10)

  return (
    <>
      {adding !== null && counts && (
        /* The forecast props are null, and that is the honest answer rather
           than a gap. Every one of them comes from GET /v1/sites/:id/lice --
           the model -- which stays on the person plane: a device that counts
           lice has no need of a four-week projection, and opening the route to
           get four labels would widen the credential to fetch decoration. The
           wizard already treats all four as optional. */
        <Telling siteId={siteId} initialDate={adding}
          counts={counts} treatments={treatments}
          windowEnd={null} origin={null} grense={null} tempHint={null}
          onSaved={(items) => setCounts(items)}
          onClose={() => { setAdding(null); void load() }} />
      )}

      <div className="card stack" style={{ gap: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h1 style={{ margin: 0, fontSize: 22 }}>{siteName}</h1>
          {onChangeSite && (
            <button className="btn ghost sm" onClick={onChangeSite}>
              {t('Another farm')}
            </button>
          )}
        </div>

        {error && <ErrorBox error={error} />}

        {canCount ? (
          <button className="btn primary"
            style={{ fontSize: 19, padding: '18px 20px' }}
            onClick={() => setAdding(today)}>
            {t('Register a count')}
          </button>
        ) : (
          <div className="notice">
            {t('This device may not register counts. Ask for a different code.')}
          </div>
        )}
      </div>

      {counts === null ? <Rows n={3} /> : (
        <Recent siteId={siteId} counts={counts} onChanged={load} />
      )}
    </>
  )
}

/** What has been entered here lately, and the one way to undo it.
 *
 *  Withdrawal rather than deletion, and it is the only correction this screen
 *  offers: the upsert behind "register a count" corrects a NUMBER against a
 *  date, but nothing corrects a count entered against the wrong DAY, and such a
 *  count anchors the model indefinitely. Somebody who fat-fingered yesterday
 *  needs to be able to say so from the walkway rather than ring the office.
 */
function Recent({
  siteId, counts, onChanged,
}: { siteId: string; counts: LiceCount[]; onChanged: () => Promise<void> }) {
  const t = useT()
  const [busy, setBusy] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<string | null>(null)

  if (counts.length === 0) {
    return (
      <div className="card">
        <p className="muted" style={{ margin: 0 }}>
          {t('Nothing counted here yet.')}
        </p>
      </div>
    )
  }

  const withdraw = async (countId: string) => {
    setBusy(countId)
    try {
      await withdrawLiceCount(siteId, countId)
      await onChanged()
    } finally {
      setBusy(null)
      setConfirming(null)
    }
  }

  return (
    <div className="card stack" style={{ gap: 4 }}>
      <h2 style={{ margin: '0 0 6px', fontSize: 16 }}>{t('Recent counts')}</h2>
      {counts.slice(0, 12).map((c) => (
        <div key={c.count_id} className="device-row">
          <div>
            <div style={{ fontWeight: 600 }}>{datoLang(c.counted_at)}</div>
            <div className="muted" style={{ fontSize: 13 }}>
              {c.adult_female ?? '—'} {t('adult female per fish')}
              {c.counted_by ? ` · ${c.counted_by}` : ''}
            </div>
          </div>
          {confirming === c.count_id ? (
            <div className="row" style={{ gap: 6 }}>
              <button className="btn danger sm" disabled={busy === c.count_id}
                onClick={() => void withdraw(c.count_id)}>
                {busy === c.count_id ? t('Taking it back…') : t('Yes, take it back')}
              </button>
              <button className="btn ghost sm" onClick={() => setConfirming(null)}>
                {t('No')}
              </button>
            </div>
          ) : (
            <button className="btn ghost sm" onClick={() => setConfirming(c.count_id)}>
              {t('Take back')}
            </button>
          )}
        </div>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------ the two exits */

/** Changing scope, and leaving.
 *
 *  Two different actions, kept apart on purpose. "Bytt kode" posts a new code
 *  with the cookie still attached, so the receiver widens THIS device in place
 *  and last month's counts stay attached to the tablet that entered them.
 *  "Meld ut" destroys the session, and the next code typed here makes a new
 *  device with a new id -- which is what you want for a tablet being sold, and
 *  what you emphatically do not want for one moving from a farm to an area.
 */
function Foot() {
  const t = useT()
  const [leaving, setLeaving] = useState(false)
  return (
    <div className="device-foot">
      <button className="btn ghost sm" onClick={() => navigate('/koble')}>
        {t('Enter a new code')}
      </button>
      <LangSwitch />
      {leaving ? (
        <div className="stack" style={{ gap: 8 }}>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            {t('This device will be disconnected. Counts already entered stay where they are, still under this name.')}
          </p>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn danger sm"
              onClick={() => void deviceSignout().finally(() => window.location.assign('/koble'))}>
              {t('Yes, disconnect it')}
            </button>
            <button className="btn ghost sm" onClick={() => setLeaving(false)}>
              {t('Cancel')}
            </button>
          </div>
        </div>
      ) : (
        <button className="btn ghost sm" onClick={() => setLeaving(true)}>
          {t('Disconnect this device')}
        </button>
      )}
    </div>
  )
}
